import { React, useState, useContext, useEffect, useRef } from "react";
import { Drawer, Button, Flex, Plus, Text, Checkbox, Tooltip, DeviceStatus, TrashSimple, MTable, useRowSelection } from "@cisco/spaces-dash-lib-styleguide";
import { I18nContext } from "../../../contexts/i18nContext";
import useLoader from "../../../hooks/useLoader";
import useNotification from "../../../hooks/useNotification";
import * as BusinessHierarchyService from "../../../service/business-hierarchy-service";
import WarningConfirmationAlert from "../warning-confirmation-alert/warning-confirmation-alert";
import "./add-edit-controller.scss";
import "../../../common/common_style.scss";
import "../../../common/global_style.scss";

function AddEditController(props) {
  const i18n = useContext(I18nContext);
  const { showAddEditController, cancelAddEditCtrlPopup, selectedNode } = props;
  const { selectedRows, setRowSelection } = useRowSelection();
  const [node, setNode] = useState(selectedNode);
  const [locationId, setLocationId] = useState(selectedNode.data.id);
  const { setLoading } = useLoader();
  const { setToastMessage } = useNotification();
  const [autoNetworkCreation, setAutoNetworkCreation] = useState(false);
  const [autoNetworkCreationFromDB, setAutoNetworkCreationFromDB] = useState(false);
  const [dayZero, setDayZero] = useState(true);
  const [currentWLCControllersUI, setCurrentWLCControllersUI] = useState([]);
  const [currentWLCControllersUIState, setCurrentWLCControllersUIState] = useState(true);
  const [initialWLCControllerCountFromDB, setInitialWLCControllerCountFromDB] = useState(0);
  const [pageView, setPageView] = useState("list");
  const controllerToDelete = useRef({});
  const [showWarningConfirmation, setShowWarningConfirmation] = useState(false);
  const [primaryIp, setPrimaryIp] = useState("");
  const [primaryIpApSize, setPrimaryIpApSize] = useState(0);
  const [additionalWLCControllersUI, setAdditionalWLCControllersUI] = useState([]);
  const [noRemainingWlcIps, setNoRemainingWlcIps] = useState(true);
  const [optedAdditionalAps, setOptedAdditionalAps] = useState([]);
  let unAddedSecondaryIps = [];
  let addMoreAddlIpsList = [];
  let addMoreAddlIps = [];
  let remainingWlcIps = [];

  const toggleAutoCreation = () => {
    setAutoNetworkCreation((current) => !current);
  };

  const addEditWLCController = () => {
    setLoading(true);
    setCurrentWLCControllersUIState(true);
    BusinessHierarchyService.getWLCControllers(locationId)
      .then((response) => {
        if (response.status) {
          const { primary, apSize = 0, secondary, autoCreateNode, unAddedSecondary } = response.data;

          setPrimaryIp(primary);
          setPrimaryIpApSize(apSize);
          setAutoNetworkCreation(autoCreateNode);
          setAutoNetworkCreationFromDB(autoCreateNode);
          const combinedFinalListIps = Object.entries(secondary || {}).map(([ip, size]) => ({ ip, apSize: size }));
          if (primary) {
            combinedFinalListIps.unshift({ ip: primary, apSize });
          }
          unAddedSecondaryIps = Object.entries(unAddedSecondary || {}).map(([ip, size]) => ({ ip, apSize: size }));
          setCurrentWLCControllersUI(combinedFinalListIps);
          setCurrentWLCControllersUIState(false);
          setDayZero(combinedFinalListIps.length === 0);
          setInitialWLCControllerCountFromDB(combinedFinalListIps.length);
        } else {
          setToastMessage(response.message, 5000, "negative");
        }
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
        console.log(`Add/Edit controller: Error on addEditWLCController${error}`);
      });
  };

  const triggerDeleteController = (index) => {
    controllerToDelete.current = {
      index,
      controller: currentWLCControllersUI[index]
    };
    setShowWarningConfirmation((current) => !current);
  };

  const cancelDelete = () => {
    controllerToDelete.current = {};
    setShowWarningConfirmation((current) => !current);
  };

  const updateController = (type) => {
    const secondaryIPArray = [];
    const wlcObj = {
      type: "wlc",
      locationId
    };

    switch (type) {
      case "primary":
        wlcObj.secondary = secondaryIPArray;
        break;
      case "secondary":
        wlcObj.primary = primaryIp;
        wlcObj.secondary = secondaryIPArray;
        break;
      case "autoCreate":
        wlcObj.autoCreateNode = autoNetworkCreation;
        break;
      default:
        console.error(`Unhandled type: ${type}`);
        return;
    }
    setCurrentWLCControllersUIState(true);
    BusinessHierarchyService.updateController(wlcObj)
      .then((response) => {
        if (response.status) {
          const { primary, apSize, secondary, autoCreateNode } = response.data;
          setPrimaryIp(primary);
          setPrimaryIpApSize(apSize);
          setAutoNetworkCreation(autoCreateNode);
          setAutoNetworkCreationFromDB(autoCreateNode);
          const combinedFinalListIps = Object.entries(secondary || {}).map(([ip, size]) => ({ ip, apSize: size }));
          if (primary && apSize) {
            combinedFinalListIps.unshift({ ip: primary, apSize });
          }

          setCurrentWLCControllersUI(combinedFinalListIps);
          setCurrentWLCControllersUIState(false);
          setInitialWLCControllerCountFromDB(combinedFinalListIps.length);
          setDayZero(combinedFinalListIps.length === 0);
          setToastMessage(response.message, 5000, "positive");
        } else {
          setToastMessage(response.message, 5000, "negative");
          console.log("Error on updateController");
        }
        setLoading(false);
        addEditWLCController();
      })
      .catch((error) => {
        setLoading(false);
        setToastMessage(i18n.t("add-edit-controller.unable-to-update-cuwn-msg"), 5000, "negative");
        console.log(`Error on updateController : ${error}`);
      });
  };

  const deleteController = (controller) => {
    BusinessHierarchyService.deleteController(controller)
      .then((response) => {
        if (response.status) {
          setToastMessage(response.message, 5000, "positive");
        } else {
          setToastMessage(response.message, 5000, "negative");
        }
        setLoading(false);
        setShowWarningConfirmation((current) => !current);
        addEditWLCController();
      })
      .catch((error) => {
        setShowWarningConfirmation((current) => !current);
        setToastMessage(i18n.t("add-edit-controller.unable-to-delete-controller"), 5000, "negative");
        setLoading(false);
        console.log(`Error on Delete Controller: ${error}`);
      });
  };

  const implictControllerDelete = () => {
    setLoading(true);
    const [toDelete, controllerToUpdate, ...remainingWlcList] = currentWLCControllersUI;
    const secondaryIPArray = remainingWlcList.map((obj) => obj.ip || obj);
    secondaryIPArray.push(toDelete.ip);
    const wlcObj = {
      type: "wlc",
      locationId,
      primary: controllerToUpdate.ip,
      secondary: secondaryIPArray,
      networks: []
    };
    BusinessHierarchyService.updateController(wlcObj)
      .then((response) => {
        if (response.status) {
          const controller = {
            locationId,
            controllerIp: toDelete.ip
          };
          deleteController(controller);
        } else {
          setToastMessage(i18n.t("add-edit-controller.unable-to-delete-controller"), 5000, "negative");
          console.error("Error on implicit controller update");
          setShowWarningConfirmation((current) => !current);
        }
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
        setToastMessage(i18n.t("add-edit-controller.unable-to-delete-controller"), 5000, "negative");
        console.log(`Error on implict controller update : ${error}`);
        setShowWarningConfirmation((current) => !current);
      });
  };

  const getCellValueWithToolTip = ({ row }) => {
    return (
      <Flex justify="space-between">
        <Text>{row.original.apSize}</Text>
        <Button
          onClick={() => {
            triggerDeleteController(row.id);
          }}
          disabled={currentWLCControllersUI.length == 1}
          icon={<TrashSimple size={16} weight="bold" />}
          kind="tertiary"
        />
      </Flex>
    );
  };

  const toggleBackListControllerView = () => {
    setPageView("list");
    setRowSelection({});
    setCurrentWLCControllersUIState(true);
    addEditWLCController();
  };

  const getAvailableAdditionalIps = () => {
    addMoreAddlIpsList = [];
    addMoreAddlIps = [];
    setLoading(true);
    setNoRemainingWlcIps(true);
    BusinessHierarchyService.getAvailableWLCControllers(locationId)
      .then((response) => {
        if (response.status) {
          const responseData = response.data;
          remainingWlcIps = [];
          const wlcIps = [];
          // let index = 0;
          // let selectedIndex = -1;
          // const secondaryIps = [];
          Object.entries(responseData).forEach(([key, value]) => {
            if (key === primaryIp) {
              // Extract secondary IPs if primary IP matches
              const secondary = Object.entries(value.secondary || {}).map(([secKey, secValue]) => ({
                ip: secKey,
                apSize: secValue
              }));

              // Create the wlcIps entry
              wlcIps.push({
                ip: key,
                apSize: value.apSize,
                secondary
              });
            } else {
              // Add to remainingWlcIps if primary IP doesn't match
              remainingWlcIps.push({
                ip: key,
                apSize: value.apSize
              });
            }
          });

          // Determine addMoreAddlIps based on the presence of the selectedIndex
          addMoreAddlIps = wlcIps.length > 0 ? wlcIps[0].secondary : [];
          const wlcIps1 = [];
          Object.entries(responseData).forEach(([key, value]) => {
            if (key !== primaryIp) {
              wlcIps1.push({
                ip: key,
                apSize: value.apSize
              });
            }
          });
          remainingWlcIps = wlcIps1;

          /* Remove for additional controller ip duplicate */
          addMoreAddlIps.forEach((obj1) => {
            remainingWlcIps.forEach((obj2, ipIndex) => {
              if (obj1.ip == obj2.ip) {
                remainingWlcIps.splice(ipIndex, 1);
              }
            });
          });

          /* Remove for unadded additional controller ip duplicate */
          unAddedSecondaryIps.forEach((obj1) => {
            remainingWlcIps.forEach((obj2, ipIndex1) => {
              if (obj1.ip == obj2.ip) {
                remainingWlcIps.splice(ipIndex1, 1);
              }
            });
          });

          /* concat all list into single list */
          addMoreAddlIpsList = remainingWlcIps.concat(addMoreAddlIps);
          addMoreAddlIpsList = addMoreAddlIpsList.concat(unAddedSecondaryIps);

          /* secondaryIpSelected */
          const updatedAddlIpsList = addMoreAddlIpsList.map((element) => ({
            ...element,
            selected: false
          }));
          addMoreAddlIpsList = updatedAddlIpsList;
          setAdditionalWLCControllersUI(addMoreAddlIpsList);
          if (addMoreAddlIpsList.length > 0) {
            setNoRemainingWlcIps(false);
          }
        } else {
          setToastMessage(response.message, 5000, "negative");
        }
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
        console.log(`Add/Edit controller: Error on getAvailableAdditionalIps${error}`);
      });
  };

  const showAddControllerView = () => {
    setPageView("add");
    getAvailableAdditionalIps();
  };

  const confirmDelete = () => {
    console.log(controllerToDelete.current);
    setLoading(true);
    if (controllerToDelete.current.index > 0) {
      const controller = {
        locationId,
        controllerIp: controllerToDelete.current.controller.ip
      };
      deleteController(controller);
    } else {
      implictControllerDelete();
    }
  };

  // const selectedAdditionalAps = (state) => {
  //   setOptedAdditionalAps([]);
  //   const selectedAps = Object.keys(state)
  //     .filter((key) => state[key])
  //     .map((key) => additionalWLCControllersUI[key]);
  //   setOptedAdditionalAps(selectedAps);
  // };

  const addControllers = () => {
    setLoading(true);
    let primaryApName = primaryIp;
    const secondaryIPArray = [];
    if (!primaryApName && optedAdditionalAps.length > 0) {
      const toBePrimary = optedAdditionalAps.shift();
      primaryApName = toBePrimary.ip;
    }
    const existingWlcIpsFiltered = primaryApName ? currentWLCControllersUI.filter((obj) => obj.ip !== primaryApName) : currentWLCControllersUI;
    const combinedIPs = [...optedAdditionalAps, ...existingWlcIpsFiltered];
    combinedIPs.forEach((obj) => {
      secondaryIPArray.push(obj.ip || obj);
    });
    const wlcObj = {
      type: "wlc",
      locationId,
      primary: primaryApName,
      secondary: secondaryIPArray,
      networks: []
    };
    BusinessHierarchyService.updateController(wlcObj)
      .then((response) => {
        if (response.status) {
          setToastMessage(response.message, 5000, "positive");
          toggleBackListControllerView();
        } else {
          setToastMessage(response.message, 5000, "negative");
          console.log("Error on updateController while adding additional controllers");
        }
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
        setToastMessage(i18n.t("add-edit-controller.unable-to-update-cuwn-msg"), 5000, "negative");
        console.log(`Error on updateController : ${error}`);
      });
  };

  useEffect(() => {
    setOptedAdditionalAps([]);
    const selectedAps = Object.keys(selectedRows)
      .filter((key) => selectedRows[key])
      .map((key) => additionalWLCControllersUI[key]);
    setOptedAdditionalAps(selectedAps);
  }, [selectedRows]);

  useEffect(() => {
    console.log("AddEditController Mount");
    console.log(primaryIpApSize);
    if (node && node.data && node.data.id) {
      addEditWLCController();
    }
    setRowSelection({});
    return () => {
      console.log("AddEditController Destroy");
      setRowSelection({});
    };
  }, []);

  return (
    <Drawer size="lg" className={`test-drawer-visible ${showWarningConfirmation ? "test-drawer-hidden" : ""}`} isOpen={showAddEditController}>
      {showWarningConfirmation && (
        <WarningConfirmationAlert
          showWarningConfirmation={showWarningConfirmation}
          cancelDelete={cancelDelete}
          confirmDelete={confirmDelete}
          controller={controllerToDelete}
          content="content"
          setNode={setNode}
          setLocationId={setLocationId}
        />
      )}
      <Drawer.Heading style={{ "margin-bottom": "20px" }}>
        {pageView == "list" && i18n.t("add-edit-controller.add-edit-controller-text")}
        {pageView == "add" && i18n.t("add-edit-controller.add-controllers-text")}
      </Drawer.Heading>
      <Drawer.Content>
        <Flex direction="vertical" gap={16}>
          {pageView == "list" && (
            <Flex direction="vertical" gap={16}>
              <Flex justify="space-between">
                <Flex align="center" gap={16}>
                  <Flex gap={8}>
                    <Checkbox size="sm" onClick={toggleAutoCreation} checked={autoNetworkCreation}>
                      {i18n.t("add-edit-controller.add-location-automatically-in-hierarchy-text")}
                    </Checkbox>
                    <Tooltip
                      placement="bottom"
                      style={{ "z-index": "99999" }}
                      title={
                        <Flex direction="vertical" gap={12} align="left">
                          <Text size="p1" color="light" style={{ color: "#FFFF" }}>
                            {i18n.t("add-edit-controller.add-location-auto-text")}
                          </Text>
                          <Text size="p4" color="light" style={{ color: "#FFFF" }}>
                            {i18n.t("add-edit-controller.location-auto-add-text")}
                          </Text>
                        </Flex>
                      }
                    >
                      <span className="hover-target">
                        <DeviceStatus status="info" />
                      </span>
                    </Tooltip>
                  </Flex>
                </Flex>
                <Flex gap={8}>
                  <Button icon={<Plus size={16} weight="bold" />} kind="secondary" onClick={showAddControllerView}>
                    {i18n.t("add-edit-controller.add-controller-text")}
                  </Button>
                </Flex>
              </Flex>
              {dayZero && !currentWLCControllersUI.length > 0 && (
                <Flex
                  style={{
                    height: "500px",
                    "background-color": "#f7f7f7",
                    "padding-left": "42%",
                    "padding-top": "30%"
                  }}
                >
                  <Text size="p3" weight="regular">
                    {i18n.t("add-edit-controller.no-controllers-added-text")}
                  </Text>
                </Flex>
              )}
              {!dayZero && currentWLCControllersUI.length > 0 && (
                <MTable.Pagination key={currentWLCControllersUI.length} currentPage={1} defaultRecordsOption="10" showNavigation="all" showTotal showOptions>
                  <MTable
                    columns={[
                      {
                        accessorKey: "ip",
                        header: i18n.t("add-edit-controller.controller-name-text"),
                        meta: {
                          align: "left"
                        },
                        size: 5
                      },
                      {
                        accessorKey: "apSize",
                        header: i18n.t("add-edit-controller.number-of-aps-text"),
                        meta: {
                          align: "left"
                        },
                        size: 5,
                        cell: getCellValueWithToolTip
                      }
                    ]}
                    data={currentWLCControllersUI}
                    isLoading={currentWLCControllersUIState || primaryIp == ""}
                    enablePagination
                  />
                </MTable.Pagination>
              )}
            </Flex>
          )}
          {pageView == "add" && (
            <Flex direction="vertical" gap={16}>
              {noRemainingWlcIps && !additionalWLCControllersUI.length > 0 && (
                <Flex
                  justify="center"
                  direction="vertical"
                  style={{
                    height: "500px",
                    "background-color": "#f7f7f7"
                  }}
                >
                  <Flex direction="vertical" justify="center" align="center">
                    <Text size="p3" weight="regular" color="light">
                      {i18n.t("add-edit-controller.no-controllers-available-text")}
                    </Text>
                    <Text size="p3" weight="regular" color="light">
                      {i18n.t("add-edit-controller.go-to-setup-text")}
                    </Text>
                  </Flex>
                </Flex>
              )}
              {!noRemainingWlcIps && additionalWLCControllersUI.length > 0 && (
                <Flex direction="vertical" gap={28}>
                  <Text>{i18n.t("add-edit-controller.choose-the-controller-text")}</Text>
                  <MTable.Pagination key={additionalWLCControllersUI.length} currentPage={1} defaultRecordsOption="10" showNavigation="all" showTotal showOptions>
                    <MTable
                      columns={[
                        {
                          accessorKey: "ip",
                          header: i18n.t("add-edit-controller.controller-ip-text"),
                          meta: {
                            align: "left"
                          },
                          cell: (data) => <Text>{data.getValue()}</Text>
                        },
                        {
                          accessorKey: "apSize",
                          header: i18n.t("add-edit-controller.number-of-aps-text"),
                          meta: {
                            align: "left"
                          },
                          cell: (data) => <Text>{data.getValue()}</Text>
                        }
                      ]}
                      data={additionalWLCControllersUI}
                      onRowSelectionChange={setRowSelection}
                      rowSelection={selectedRows}
                      selectableRows
                      // getSelectedRows={(state) => {
                      //   selectedAdditionalAps(state);
                      // }}
                    />
                  </MTable.Pagination>
                </Flex>
              )}
            </Flex>
          )}
        </Flex>
      </Drawer.Content>
      <Drawer.Footer>
        {pageView == "list" && (
          <Flex style={{ width: "100%" }}>
            <Button.Group>
              <Button
                kind="primary"
                disabled={autoNetworkCreation == autoNetworkCreationFromDB && initialWLCControllerCountFromDB == currentWLCControllersUI.length}
                onClick={() => {
                  updateController("autoCreate");
                }}
              >
                {i18n.t("add-edit-controller.save-text")}
              </Button>
              <Button kind="tertiary" onClick={cancelAddEditCtrlPopup}>
                {i18n.t("business-location-details.cancel-text")}
              </Button>
            </Button.Group>
          </Flex>
        )}

        {pageView == "add" && (
          <Flex style={{ width: "100%" }}>
            <Button.Group>
              <Button kind="primary" disabled={optedAdditionalAps.length == 0} onClick={addControllers}>
                {i18n.t("add-edit-controller.add-text")}
              </Button>
              <Button kind="tertiary" onClick={toggleBackListControllerView}>
                {i18n.t("add-edit-controller.back-text")}
              </Button>
            </Button.Group>
          </Flex>
        )}
      </Drawer.Footer>
    </Drawer>
  );
}

export default AddEditController;
